import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, X, ArrowRight, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";

/**
 * Phase 25b · Cart widget for the Header.
 *
 * Icon with badge count → dropdown with cart items + preview + checkout link.
 */
const API = process.env.REACT_APP_BACKEND_URL || "";

async function fetchAssetsMeta(ids) {
  if (!ids?.length) return [];
  try {
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const r = await fetch(
            `${API}/api/marketplace/paid/asset/${encodeURIComponent(id)}`,
            { credentials: "include" },
          );
          if (!r.ok) return null;
          return r.json();
        } catch {
          return null;
        }
      }),
    );
    return results.filter(Boolean);
  } catch {
    return [];
  }
}

async function fetchPreview(ids) {
  try {
    const r = await fetch(`${API}/api/marketplace/cart/preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ asset_ids: ids }),
    });
    if (!r.ok) return null;
    return r.json();
  } catch {
    return null;
  }
}

export function CartWidget() {
  const { ids, count, remove } = useCart();
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open || ids.length === 0) {
      setAssets([]);
      setPreview(null);
      return;
    }
    let cancelled = false;
    setBusy(true);
    (async () => {
      const [meta, prev] = await Promise.all([
        fetchAssetsMeta(ids),
        fetchPreview(ids),
      ]);
      if (!cancelled) {
        setAssets(meta);
        setPreview(prev);
        setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, ids]);

  const eur = (c) => (Number(c || 0) / 100).toFixed(2).replace(".", ",");

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="cart-widget-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center rounded-full border border-[var(--color-border)] bg-[var(--color-card)] p-2 hover:border-[var(--color-primary)]/50 transition-colors"
        aria-label="Cart"
      >
        <ShoppingCart className="h-4 w-4 text-[var(--color-secondary)]" />
        {count > 0 && (
          <span
            data-testid="cart-widget-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-white hover:bg-[#EDEDED] text-black text-[10px] font-semibold flex items-center justify-center tabular-nums"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="cart-widget-dropdown"
          className="absolute right-0 mt-2 w-80 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-text-secondary">
              Cart ({count})
            </span>
            {count > 0 && (
              <Link
                to="/cart"
                onClick={() => setOpen(false)}
                data-testid="cart-widget-view-all"
                className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] hover:underline"
              >
                View all
              </Link>
            )}
          </div>
          {count === 0 ? (
            <div className="p-8 text-center text-xs text-text-secondary">
              Il carrello è vuoto. Aggiungi asset dal marketplace.
            </div>
          ) : (
            <>
              <div className="max-h-64 overflow-y-auto">
                {busy && (
                  <div className="p-4 text-center text-xs text-text-secondary">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Caricamento…
                  </div>
                )}
                {!busy && assets.map((a) => (
                  <div
                    key={a.asset_id}
                    data-testid={`cart-item-${a.asset_id}`}
                    className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1 pr-2">
                      <div className="text-sm font-medium text-[var(--color-text-main)] truncate">
                        {a.title || a.asset_id}
                      </div>
                      <div className="text-[10px] text-text-secondary">
                        ${eur(a.price_eur_cents)} · {a.asset_ai_type || a.asset_type}
                      </div>
                    </div>
                    <button
                      data-testid={`cart-item-remove-${a.asset_id}`}
                      onClick={() => remove(a.asset_id)}
                      className="text-text-secondary hover:text-[var(--color-alert)]"
                      aria-label="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              {preview && (
                <div className="px-4 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40 space-y-1">
                  <div className="flex items-center justify-between text-[10px] text-text-secondary uppercase tracking-widest">
                    <span>Subtotal</span>
                    <span className="tabular-nums">${eur(preview.subtotal_cents)}</span>
                  </div>
                  {preview.discount_pct > 0 && (
                    <div
                      data-testid="cart-widget-discount"
                      className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--color-success)]"
                    >
                      <span>Bundle discount</span>
                      <span className="tabular-nums">
                        −{preview.discount_pct}% (−${eur(preview.discount_cents)})
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs font-semibold">Total</span>
                    <span
                      data-testid="cart-widget-total"
                      className="text-sm font-semibold text-[var(--color-secondary)] tabular-nums"
                    >
                      ${eur(preview.total_cents)}
                    </span>
                  </div>
                </div>
              )}
              <Link
                to="/cart"
                onClick={() => setOpen(false)}
                data-testid="cart-widget-checkout"
                className="block w-full px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest bg-white hover:bg-[#EDEDED] text-black hover:opacity-90"
              >
                <span className="inline-flex items-center gap-1">
                  Checkout <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CartWidget;
